import debounce from 'lodash/debounce';
import union from 'lodash/union';
import uniq from 'lodash/uniq';
import uniqueId from 'lodash/uniqueId';
import { computed, ref } from 'vue';

import * as socialListeningApi from '@/apis/social-listening';
import { DEFAULT_KEYWORD_GROUPS } from '@/app/socialListening/constants';
import {
  buildMessageForParseTreeError,
  handleTextEditorErrorsUsingKeywordGroups,
  handleTextEditorErrorsUsingParseTree,
} from '@/app/socialListening/utils/keyword-composer-utils';
import { parseToQuery, parseTreeToGroups } from '@/app/socialListening/utils/parsing';
import { debounceInputDelay } from '@/config';
import { useBooleanExpressionEditorStore } from '@/stores/boolean-expression-editor';
import { useIdentityStore } from '@/stores/identity';

// Single-token terms only: reconstructGroupImages round-trips these keys via split(' OR ').
function buildImageExpressionKey(terms) {
  return `(${[...terms].sort((a, b) => a.localeCompare(b)).join(' OR ')})`;
}

function normalizeExpressionTerms(keywordsExpression) {
  return uniq(
    keywordsExpression
      .replace(/[()]/g, '')
      .split(/\s+(?:OR|AND)\s+/i)
      .map((t) => t.trim())
      .filter(Boolean),
  ).sort((a, b) => a.localeCompare(b));
}

// Array form (not object) so humps doesn't decamelize the OR-expression keys.
// visFeatures and pinToFirstGroup are persisted: visFeatures is needed for order_by derivation
// on reload; pinToFirstGroup is needed for display/prune on reload.
export function serializeImageKeywordMap(map) {
  if (!map || typeof map !== 'object') return [];
  return Object.entries(map)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([expression, value]) => {
      const entry = typeof value === 'string' ? { url: value } : { ...value };
      return { expression, ...entry };
    });
}

// Back-compat: also accepts the legacy plain-object form ({ expression: url }).
export function deserializeImageKeywordMap(value) {
  if (!value) return {};
  if (Array.isArray(value)) {
    return Object.fromEntries(value.map(({ expression, ...rest }) => [expression, rest]));
  }
  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, typeof v === 'string' ? { url: v } : { ...v }]),
    );
  }
  return {};
}

const parseExpressionKeywords = (key) => key.replace(/^\(|\)$/g, '').split(' OR ');

const groupContainsAllKeywords = (groupKeywordSet, keywords) =>
  keywords.length > 0 && keywords.every((t) => groupKeywordSet.has(t));

function reconstructGroupImages(groups, imageKeywordMap) {
  if (!imageKeywordMap || Object.keys(imageKeywordMap).length === 0) return groups;

  const parsedEntries = Object.entries(imageKeywordMap).map(([key, value]) => ({
    expression: key,
    keywords: parseExpressionKeywords(key),
    ...(typeof value === 'string' ? { url: value } : (value ?? {})),
  }));

  return groups.map((group, idx) => {
    const groupKeywordSet = new Set(group.keywords ?? []);
    const images = parsedEntries.filter((entry) =>
      // pinToFirstGroup: imageKeywordsExpression is AND-joined across all images, so no single
      // group contains all its terms — pin every image to group 0 for display instead.
      entry.pinToFirstGroup ? idx === 0 : groupContainsAllKeywords(groupKeywordSet, entry.keywords),
    );

    return images.length > 0 ? { ...group, images } : group;
  });
}

// Forgiving prune: keep in-memory entries during editing, but exclude orphans from the save payload.
function pruneImageKeywordMap(groups, map) {
  if (!map || typeof map !== 'object') return {};
  const groupKeywordSets = groups.map((g) => new Set(g.keywords ?? []));
  return Object.fromEntries(
    Object.entries(map).filter(([key, value]) => {
      // pinToFirstGroup entries are immune to pruning (AND-expression image from Overview).
      if (value?.pinToFirstGroup) return true;
      const keywords = parseExpressionKeywords(key);
      return groupKeywordSets.some((set) => groupContainsAllKeywords(set, keywords));
    }),
  );
}

export function buildTopicQuery(imageExpr, builderExpr) {
  const img = imageExpr?.trim();
  const builder = builderExpr?.trim();
  if (img && builder) return `(${img}) OR (${builder})`;
  return img || builder || '';
}

export function useListeningKeywords() {
  const booleanExpressionEditorStore = useBooleanExpressionEditorStore();
  const identityStore = useIdentityStore();

  const listKeywordsFromGroups = (groups) =>
    groups.reduce((acc, { keywords }) => [...acc, ...keywords], []);
  const previousKeywords = ref([]);

  const plainTextInput = ref('');
  const quickSearchTextInput = ref('');
  const keywordGroups = ref(
    [...DEFAULT_KEYWORD_GROUPS].map((group) => ({ ...group, id: uniqueId() })),
  );
  const currentKeywords = computed(() => listKeywordsFromGroups(keywordGroups.value));
  const keywordExpressionErrors = ref([]);
  const isValidating = ref(false);

  const imageKeywordMap = ref({});
  const imageKeywordsExpression = ref('');
  const imageKeywordMapPayload = computed(() =>
    serializeImageKeywordMap(pruneImageKeywordMap(keywordGroups.value, imageKeywordMap.value)),
  );

  const imageVisFeatures = computed(() =>
    Object.values(imageKeywordMap.value)
      .map((e) => e.visFeatures)
      .filter(Boolean),
  );

  const canSaveKeywords = computed(
    () => !isValidating.value && !keywordExpressionErrors.value?.length,
  );

  // Methods
  const addKeywordBooleanExpressionErrors = (newErrors = []) => {
    keywordExpressionErrors.value = [...keywordExpressionErrors.value, ...newErrors];
  };

  const handleParseTreeError = (error, hideErrorLine = false) => {
    booleanExpressionEditorStore.errorHighlightForSyntaxError(error, plainTextInput.value);
    const { line } = error.location;
    const { unexpected, expected } = error.tokens;
    if (unexpected || expected.length) {
      const errorLine = hideErrorLine ? undefined : line;
      const formattedError = {
        error: buildMessageForParseTreeError(unexpected, expected, errorLine),
      };
      addKeywordBooleanExpressionErrors([formattedError]);
    }
  };

  const handleKeywordBooleanExpressionErrors = (newGroups, parseTree, expression) => {
    let errors = [];
    // using keyword groups
    const groups = newGroups || keywordGroups.value;
    if (groups?.length) {
      errors = errors.concat(handleTextEditorErrorsUsingKeywordGroups(groups));
      booleanExpressionEditorStore.highlightDuplicateKeywords(groups);
    }
    // using parse tree
    if (parseTree) {
      const parseTreeErrors = handleTextEditorErrorsUsingParseTree(parseTree);
      if (parseTreeErrors?.length) {
        errors = errors.concat(parseTreeErrors);
      }
    }
    // using expression string
    if (expression) {
      const booleanEditorExpressionStore = useBooleanExpressionEditorStore();
      const expressionErrors =
        booleanEditorExpressionStore.handleTextEditorErrorsUsingExpression(expression);
      if (expressionErrors?.length) {
        errors = errors.concat(expressionErrors);
      }
    }
    addKeywordBooleanExpressionErrors(errors);
  };

  const loadSavedTopicKeywords = async ({ keywordsAndHashtags: mergedQuery }, meta) => {
    isValidating.value = true;
    keywordExpressionErrors.value = [];
    booleanExpressionEditorStore.resetTokenErrorMap();

    // Older topics nested meta one level deeper; tolerate both shapes.
    const rawImageKeywordMap = meta?.imageKeywordMap ?? meta?.meta?.imageKeywordMap ?? [];
    const savedImageKeywordMap = deserializeImageKeywordMap(rawImageKeywordMap);

    // Hydrate the image expression ref from meta.
    imageKeywordsExpression.value =
      meta?.imageKeywordsExpression ?? meta?.meta?.imageKeywordsExpression ?? '';

    // Builder expression: prefer the stored builder-only text; fall back to the merged query
    // for legacy/non-image topics that have no editorText.
    const builderExpression =
      meta?.keywordAndHashtagsEditorText ?? // canonical (single-topic create + update)
      meta?.meta?.keywordsAndHashtagsEditorText ?? // nested (Multiple/Competitive templates)
      meta?.keywordsAndHashtagsEditorText ?? // legacy/typo fallback
      mergedQuery;

    const plainText =
      meta?.keywordAndHashtagsEditorText ?? meta?.meta?.keywordsAndHashtagsEditorText ?? undefined;

    // Parse the builder-only expression to reconstruct groups (never the merged query).
    const expression = builderExpression;

    try {
      // Always restore the saved images, even for an image-only topic whose builder
      // expression is empty (otherwise the thumbnail wouldn't reappear on edit).
      imageKeywordMap.value = savedImageKeywordMap;
      if (expression) {
        const organizationId = identityStore.currentBrand.organizationId;
        const res = await socialListeningApi.getParseTree({
          organizationId,
          payload: { expression },
        });

        const parsedGroups = parseTreeToGroups(res.data.data);
        keywordGroups.value = reconstructGroupImages(parsedGroups, savedImageKeywordMap);
        previousKeywords.value = listKeywordsFromGroups(keywordGroups.value);
        plainTextInput.value = plainText ?? parseToQuery(keywordGroups.value) ?? '';
        quickSearchTextInput.value = parseToQuery(keywordGroups.value) ?? '';
        handleKeywordBooleanExpressionErrors(undefined, res?.data?.data, expression);
      } else {
        // No builder keywords (image-only): attach saved images to the default groups.
        keywordGroups.value = reconstructGroupImages(keywordGroups.value, savedImageKeywordMap);
        previousKeywords.value = listKeywordsFromGroups(keywordGroups.value);
      }
    } catch (err) {
      const parseTreeError = err.response.data?.errors?.json?.expression?.error;
      if (err.response.status === 400 && parseTreeError) {
        handleParseTreeError(parseTreeError, true);
      }
    } finally {
      isValidating.value = false;
    }
  };

  const getParseTree = async (expression) => {
    isValidating.value = true;
    keywordExpressionErrors.value = [];
    booleanExpressionEditorStore.resetTokenErrorMap();
    try {
      if (expression) {
        plainTextInput.value = expression;
        const organizationId = identityStore.currentBrand.organizationId;
        const res = await socialListeningApi.getParseTree({
          organizationId,
          payload: { expression },
        });
        // Re-apply image overlays so they survive parse refreshes.
        keywordGroups.value = reconstructGroupImages(
          parseTreeToGroups(res.data.data),
          imageKeywordMap.value,
        );
        quickSearchTextInput.value = expression;
        handleKeywordBooleanExpressionErrors(undefined, res?.data?.data, expression);
      } else {
        // Empty expression: keep any attached image on the default groups (image-only topic).
        keywordGroups.value = reconstructGroupImages(
          [...DEFAULT_KEYWORD_GROUPS],
          imageKeywordMap.value,
        );
        plainTextInput.value = '';
        quickSearchTextInput.value = '';
      }
    } catch (err) {
      const parseTreeError = err.response.data?.errors?.json?.expression?.error;
      if (err.response.status === 400 && parseTreeError) {
        handleParseTreeError(parseTreeError, true);
      }
    } finally {
      isValidating.value = false;
    }
  };

  const validateExpression = async (expression) => {
    try {
      isValidating.value = true;
      keywordExpressionErrors.value = [];
      booleanExpressionEditorStore.resetTokenErrorMap();
      const organizationId = identityStore.currentBrand.organizationId;
      const res = await socialListeningApi.getParseTree({
        organizationId,
        payload: { expression },
      });
      handleKeywordBooleanExpressionErrors(
        parseTreeToGroups(res?.data?.data),
        res?.data?.data,
        expression,
      );
    } catch (err) {
      const parseTreeError = err.response.data?.errors?.json?.expression?.error;
      if (err.response.status === 400 && parseTreeError) {
        handleParseTreeError(parseTreeError, true);
      }
    } finally {
      isValidating.value = false;
    }
  };

  const debounceEditorInput = debounce(async (expression) => {
    await getParseTree(expression);
  }, debounceInputDelay);

  const debounceValidateInput = debounce(async (expression) => {
    await validateExpression(expression);
  }, debounceInputDelay);

  const onPlainTextInputChange = (expression) => {
    plainTextInput.value = expression;
    if (!expression) {
      // Keep any attached image on the default groups (image-only topic).
      keywordGroups.value = reconstructGroupImages(
        [...DEFAULT_KEYWORD_GROUPS],
        imageKeywordMap.value,
      );
      plainTextInput.value = '';
    }
    void debounceEditorInput(expression);
  };

  const syncTextInputsFromGroups = () => {
    plainTextInput.value = parseToQuery(
      keywordGroups.value.filter((group) => group.keywords.length > 0),
    );
    quickSearchTextInput.value = plainTextInput.value;
  };

  const onGroupsUpdated = (groups) => {
    keywordGroups.value = groups;
    syncTextInputsFromGroups();
  };

  const attachImageToGroup = (groupIndex, keywordsExpression, sourceUrl, metadata = {}) => {
    // Stay synchronous: an await here would cause a "pending → empty → settled" flash in KeywordBuilderGroup.
    const { visFeatures, pinToFirstGroup, ...displayMetadata } = metadata;

    // When pinToFirstGroup is explicitly passed (swap of an Overview reference image),
    // key by sourceUrl so the entry matches the preloaded structure and survives pruning.
    let expressionKey;
    let newTerms;
    if (pinToFirstGroup) {
      expressionKey = sourceUrl;
      newTerms = [];
      // Track the image's aggregate keyword expression for query assembly.
      imageKeywordsExpression.value = keywordsExpression;
    } else {
      newTerms = normalizeExpressionTerms(keywordsExpression);
      if (newTerms.length === 0) return;
      expressionKey = buildImageExpressionKey(newTerms);
    }

    const imageEntryValue = {
      url: sourceUrl,
      ...displayMetadata,
      ...(visFeatures != null && { visFeatures }),
      ...(pinToFirstGroup && { pinToFirstGroup: true }),
    };

    keywordGroups.value = keywordGroups.value.map((group, idx) => {
      if (idx !== groupIndex) return group;

      const mergedKeywords = pinToFirstGroup
        ? (group.keywords ?? [])
        : uniq([...(group.keywords ?? []), ...newTerms]);
      const existingImages = (group.images ?? []).filter((img) => img.expression !== expressionKey);
      const newImage = {
        expression: expressionKey,
        keywords: newTerms,
        ...imageEntryValue,
      };

      return { ...group, keywords: mergedKeywords, images: [...existingImages, newImage] };
    });

    imageKeywordMap.value = { ...imageKeywordMap.value, [expressionKey]: imageEntryValue };

    syncTextInputsFromGroups();
  };

  const removeImageFromGroup = (groupIndex, expressionKey) => {
    const group = keywordGroups.value[groupIndex];
    if (!group) return;

    const imageEntry = (group.images ?? []).find((img) => img.expression === expressionKey);
    if (!imageEntry) return;

    const keywordsToRemove = new Set(imageEntry.keywords);

    // Keep keywords that are still owned by another image in this group.
    const remainingImages = (group.images ?? []).filter((img) => img.expression !== expressionKey);
    const retainedByOtherImages = new Set(remainingImages.flatMap((img) => img.keywords));

    const filteredKeywords = (group.keywords ?? []).filter(
      (kw) => !keywordsToRemove.has(kw) || retainedByOtherImages.has(kw),
    );

    keywordGroups.value = keywordGroups.value.map((g, idx) => {
      if (idx !== groupIndex) return g;
      const updatedGroup = {
        ...g,
        keywords: filteredKeywords,
        images: remainingImages,
      };
      if (updatedGroup.images.length === 0) {
        delete updatedGroup.images;
      }
      return updatedGroup;
    });

    const { [expressionKey]: _removed, ...rest } = imageKeywordMap.value;
    imageKeywordMap.value = rest;

    if (Object.keys(imageKeywordMap.value).length === 0) {
      imageKeywordsExpression.value = '';
    }

    syncTextInputsFromGroups();
  };

  // Batch-replaces ALL pinned image entries from the builder (add/remove/swap).
  // Called with the fresh proposition result after any image change in the builder.
  // Mirrors the shape preloadImageKeywordMap produces (pinToFirstGroup entries keyed by sourceUrl).
  const setReferenceImages = (imageData, imageKeywordsExpressionArg) => {
    const entries = Array.isArray(imageData) ? imageData : [];

    // Drop every existing pinned entry (those carry pinToFirstGroup), keep non-pinned entries.
    const unpinned = Object.fromEntries(
      Object.entries(imageKeywordMap.value).filter(([, v]) => !v?.pinToFirstGroup),
    );

    const seeded = { ...unpinned };

    // Seed the thumbnail/visFeatures entries whenever images are present — independent of
    // whether keywords were extracted (an image with no keywords still shows + sorts).
    entries.forEach(({ sourceUrl, logos, textInMedia, mediaDescription, visFeatures }) => {
      if (!sourceUrl) return;
      seeded[sourceUrl] = {
        url: sourceUrl,
        ...(logos?.length && { detectedLogos: logos }),
        ...(textInMedia && { detectedText: textInMedia }),
        ...(mediaDescription && { mediaDescription }),
        ...(visFeatures != null && { visFeatures }),
        pinToFirstGroup: true,
      };
    });

    imageKeywordMap.value = seeded;
    imageKeywordsExpression.value = entries.length > 0 ? (imageKeywordsExpressionArg ?? '') : '';

    // Strip stale group images first — reconstructGroupImages only adds, never removes —
    // so removing the last image actually clears the thumbnail.
    const clearedGroups = keywordGroups.value.map(({ images, ...rest }) => rest);
    keywordGroups.value = reconstructGroupImages(clearedGroups, imageKeywordMap.value);
  };

  // Replaces the map wholesale so it always reflects the current proposition.
  // Each image gets its own entry keyed by sourceUrl, pinToFirstGroup: true (immune to pruning).
  // pinToFirstGroup: imageKeywordsExpression is AND-joined across all images, so no single keyword
  // group contains all its terms — pin every image to group 0 for display instead of keyword-matching.
  const preloadImageKeywordMap = (imageData, imageKeywordsExpressionArg) => {
    const entries = Array.isArray(imageData) ? imageData : [];
    const seeded = {};

    entries.forEach(({ sourceUrl, logos, textInMedia, mediaDescription, visFeatures }) => {
      if (!sourceUrl) return;
      seeded[sourceUrl] = {
        url: sourceUrl,
        ...(logos?.length && { detectedLogos: logos }),
        ...(textInMedia && { detectedText: textInMedia }),
        ...(mediaDescription && { mediaDescription }),
        ...(visFeatures != null && { visFeatures }),
        pinToFirstGroup: true,
      };
    });

    imageKeywordMap.value = seeded;
    imageKeywordsExpression.value = entries.length > 0 ? (imageKeywordsExpressionArg ?? '') : '';
  };

  const keywordExpressionErrorMessages = computed(() =>
    union(keywordExpressionErrors.value.map(({ error }) => error)),
  );

  return {
    canSaveKeywords,
    currentKeywords,
    getParseTree,
    handleParseTreeError,
    imageKeywordMap,
    imageKeywordMapPayload,
    imageKeywordsExpression,
    imageVisFeatures,
    isValidating,
    keywordGroups,
    keywordExpressionErrorMessages,
    keywordExpressionErrors,
    loadSavedTopicKeywords,
    onPlainTextInputChange,
    onGroupsUpdated,
    plainTextInput,
    previousKeywords,
    quickSearchTextInput,
    debounceValidateInput,
    attachImageToGroup,
    removeImageFromGroup,
    preloadImageKeywordMap,
    setReferenceImages,
  };
}
